import { PendingSkeleton } from '@/shared/ui';
import { PlusIcon } from 'lucide-react';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import type { BriefStatus } from '@/entities/brief';
import { ScopedPermission } from '@/shared/ui';
import { ActionButton } from '@/shared/ui';
import { FilterChipPopover } from '@/shared/ui/FilterChipPopover';
import { SearchInput } from '@/shared/ui/SearchInput';
import { SortChipPopover } from '@/shared/ui/SortChipPopover';
import { BRIEF_SORT_CHOICES, BRIEF_STATUS_FILTER_OPTIONS } from '../../briefs.constants';
import { useTranslation } from 'react-i18next';

interface BriefsToolbarProps {
  /** Swaps the controls for placeholders while the section is still resolving. */
  pending?: boolean;
  /** Controlled search input value. The parent owns debounce + page reset. */
  searchValue: string;
  /** Fires synchronously on each keystroke with the new input value. */
  onSearchChange: (value: string) => void;
  /** Active sort choice id (see `BRIEF_SORT_CHOICES`), or `null` for the default order. */
  sortId: string | null;
  /** Fires with the new sort id (or `null` when cleared to the default). */
  onSortChange: (next: string | null) => void;
  /** Active status filter, or `null` for "All statuses". */
  statusFilter: BriefStatus | null;
  /** Fires with the new status (or `null` when cleared). */
  onStatusChange: (next: BriefStatus | null) => void;
  /** Fires when the user clicks the "New brief" button. */
  onNewBriefClick: () => void;
}

/**
 * Toolbar for the briefs list view.
 *
 * Pure controlled component — no internal state, no debounce. The parent
 * (`Briefing.tsx`) owns the search string + sort + filter, debounces search
 * via `useDebounce(SEARCH_DEBOUNCE_MS)`, and resets pagination atomically when
 * any of them change.
 *
 * Sort + Filter use the shared `SortChipPopover` / `FilterChipPopover` chips
 * (same as the Projects toolbar). Status is single-select; the parent maps the
 * value to the endpoint's `status[]` array.
 *
 * Carries the section inset itself rather than inheriting the card's header
 * slot, so all three section toolbars sit on the same 24px as the content
 * below them.
 */
export function BriefsToolbar(props: BriefsToolbarProps) {
  const { t } = useTranslation('projects');

  return (
    <div className="flex flex-row items-center justify-between gap-4 p-section">
      <PendingSkeleton pending={props.pending} className="rounded-lg">
        <div className="flex items-center gap-2">
          <SearchInput
            value={props.searchValue}
            onChange={props.onSearchChange}
            aria-label={t('projectDetail.briefsToolbar.searchBriefsByTitle')}
            placeholder={t('projectDetail.briefsToolbar.searchBriefsByTitle')}
            className="w-80"
          />

          <SortChipPopover
            options={BRIEF_SORT_CHOICES}
            value={props.sortId}
            onChange={props.onSortChange}
            resetOptionLabel={t('projectDetail.briefsToolbar.lastModifiedNewestFirst')}
          />

          <FilterChipPopover
            label={t('projectDetail.briefsToolbar.status')}
            options={BRIEF_STATUS_FILTER_OPTIONS}
            value={props.statusFilter}
            onChange={props.onStatusChange}
            resetOptionLabel={t('projectDetail.briefsToolbar.allStatuses')}
            searchable={false}
          />
        </div>
      </PendingSkeleton>

      <PendingSkeleton pending={props.pending} className="rounded-lg">
        <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
          <ActionButton size="default" onClick={props.onNewBriefClick}>
            <PlusIcon className="size-4" />
            {t('projectDetail.briefsToolbar.newBrief')}
          </ActionButton>
        </ScopedPermission>
      </PendingSkeleton>
    </div>
  );
}
